import React from 'react';
import PropTypes from 'prop-types';

import {Box, Collapse, Typography, Button, FormControl, FormLabel} from 'views/components';
import {Translate, translate} from 'views/module';

const PersonalitySection = (props) => {
  const {onAnswersChange, answers = {}, setValidity} = props;

  // Personality assessment questions in order
  const questions = [
    'adaptability_3',
    'patience_3',
    'resilience_6',
    'patience_2',
    'resilience_2',
    'resilience_3',
    'empathy_1',
    'communication_3',
    'resilience_5',
    'adaptability_2',
    'communication_1',
    'initiative_2',
    'communication_2',
    'resilience_4',
    'adaptability_1',
    'initiative_1',
    'empathy_3',
    'empathy_2',
    'resilience_1',
    'patience_1',
  ];

  // Helper to get question text by id using string interpolation
  const getQuestionTextById = (id) => translate(`caregiverScreening.Personality.Questions.${id}.Text`);

  // Calculate progress using useMemo to prevent unnecessary recalculations
  const completedCount = React.useMemo(() => Object.keys(answers).length, [answers]);
  const totalCount = React.useMemo(() => questions.length, [questions]);

  // Update validity whenever answers or questions change
  React.useEffect(() => {
    const isValid = completedCount === totalCount && totalCount > 0;
    if (setValidity) {
      setValidity(isValid);
    }
  }, [completedCount, totalCount, setValidity]);

  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    const updatedAnswers = {...answers, [questionId]: value};

    if (onAnswersChange) {
      onAnswersChange(updatedAnswers);
    }
  };

  return (
    <Box sx={{p: {md: 3, xs: 1}}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.Personality.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 2}}>
        <Translate expression='caregiverScreening.Personality.Subtitle'/>
      </Typography>

      {/* Progress Indicator */}
      <Box sx={{textAlign: 'center', mb: 3}}>
        <Typography variant="body1" color="primary.main" fontWeight={500}>
          <Translate expression='caregiverScreening.Personality.ProgressText' substitute={{completed: completedCount, total: totalCount}}/>
        </Typography>
        <Box
          sx={{
            mt: 1,
            height: 4,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              height: '100%',
              backgroundColor: 'primary.main',
              borderRadius: 2,
              width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : '0%',
              transition: 'width 0.3s ease-in-out',
            }}
          />
        </Box>
      </Box>

      {/* Questions */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        {
          questions.map((id, index) => (
            <Box
              key={id}
              sx={{
                p: {md: 3, xs: 2},
                border: '1px solid',
                borderColor: answers[id] ? 'primary.main' : 'divider',
                borderRadius: 2,
                backgroundColor: answers[id] ? 'action.hover' : 'transparent',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  component="legend"
                  sx={{
                    'fontWeight': 600,
                    'color': 'text.primary',
                    'mb': 2,
                    '&.Mui-focused': {
                      'color': 'text.primary',
                    },
                  }}
                >
                  {index + 1}. {getQuestionTextById(id)}
                </FormLabel>

                {/* Custom Rating Component */}
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', width: {xs: '100%', sm: '85%', md: '70%', lg: '70%', xl: '70%'}, gap: 2, mt: 1}}>
                    {
                      [1, 2, 3, 4, 5].map((value) => {
                        const isSelected = answers[id] === value;
                        const intensity = Math.abs(3 - value);

                        // Define responsive circle sizes using MUI breakpoints
                        const circleSize = {
                          xs: 20 + 6 * intensity, // Extra small: 20, 26, 32
                          sm: 25 + 8 * intensity, // Small: 25, 33, 41
                          md: 30 + 10 * intensity, // Medium: 30, 40, 50
                          lg: 35 + 12 * intensity, // Large: 35, 47, 59
                          xl: 40 + 15 * intensity, // Extra large: 40, 55, 70
                        };

                        const scaleLabels = {
                          1: 'Strongly Disagree',
                          2: 'Disagree',
                          3: 'Neutral',
                          4: 'Agree',
                          5: 'Strongly Agree',
                        };

                        const getButtonColor = (val) => {
                          const colors = {
                            1: '#ef4444',
                            2: '#ee8989ff',
                            3: '#acacacff',
                            4: '#91c2a3ff',
                            5: '#16a34a',
                          };
                          return colors[val];
                        };

                        const color = getButtonColor(value);

                        return (
                          <Box
                            key={value}
                            sx={{
                              'display': 'flex',
                              'flexDirection': 'column',
                              'alignItems': 'center',
                              'gap': 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                'display': 'flex',
                                'justifyContent': 'center',
                                'alignItems': 'center',
                                'width': {
                                  xs: 40,
                                  sm: 50,
                                  md: 60,
                                  lg: 70,
                                  xl: 80,
                                },
                                'height': {
                                  xs: 40,
                                  sm: 50,
                                  md: 60,
                                  lg: 70,
                                  xl: 80,
                                },
                              }}
                            >
                              <Button
                                variant={isSelected ? 'contained' : 'outlined'}
                                onClick={() => handleAnswerChange(id, value)}
                                disableElevation
                                sx={{
                                  'width': circleSize,
                                  'height': circleSize,
                                  'minWidth': circleSize,
                                  'borderRadius': '50%',
                                  'p': 0,
                                  'backgroundColor': isSelected ? color : 'transparent',
                                  'border': `2px solid ${color}`,
                                  'transition': 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    'backgroundColor': isSelected ? color : `${color}20`,
                                    'transform': 'scale(1.1)',
                                    'boxShadow': `0 4px 12px ${color}40`,
                                  },
                                }}
                              >
                              </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{fontSize: '0.7rem', maxWidth: 70}}>
                              {scaleLabels[value]}
                            </Typography>
                          </Box>
                        );
                      })
                    }
                  </Box>
                </Box>
              </FormControl>
            </Box>
          ))
        }
      </Box>

      {/* Summary */}
      <Collapse in={completedCount > 0}>
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" gutterBottom>
            <Translate expression='caregiverScreening.Personality.AssessmentProgressTitle' substitute={{completed: completedCount, total: totalCount}}/>
          </Typography>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
            {
              questions.map((id, index) => {
                const answer = answers[id];
                const isAnswered = answer !== undefined;

                return (
                  <Box
                    key={id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      backgroundColor: isAnswered ? 'primary.main' : 'action.disabled',
                      color: isAnswered ? 'primary.contrastText' : 'text.disabled',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    Q{index + 1}
                  </Box>
                );
              })
            }
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

PersonalitySection.propTypes = {
  onAnswersChange: PropTypes.func,
  setValidity: PropTypes.func,
  answers: PropTypes.objectOf(PropTypes.number),
};

export default PersonalitySection;
